import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, DEFINITION_KINDS, define } from '../types.js';

/**
 * Schedule Timeline configuration for appointment scheduling.
 */
export const schema = z
  .object({
    enabled: z
      .boolean()
      .describe(
        'Enable or disable appointment management features for clients at this level. When enabled, users can view the timeline in Portal and manage driver schedules. When disabled, all appointment-related functionality will be inactive.'
      )
      .optional(),
    full_planning_enabled: z
      .boolean()
      .describe(
        'Enable full planning and time suggestions for appointment management. When enabled, customers can take advantage of all planning features and intelligent time slot recommendations offered by the appointment management system. Note: Appointment Management must be enabled first.'
      )
      .optional(),
    strategy: z
      .string()
      .describe(
        'Select the algorithm to use for appointment scheduling. Each algorithm has different scheduling criteria and driver utilization logic.'
      )
      .optional(),
    driver_count: z
      .number()
      .int()
      .positive()
      .describe(
        'For Gig customers only: Expected number of drivers typically available for this rooftop. Used by the algorithm to calculate capacity and suggest appropriate appointment times. For SaaS customers, the system reads from their actual drivers.'
      )
      .optional(),
    move_buffer_minutes: z
      .number()
      .int()
      .nonnegative()
      .describe(
        "Minimum time gap (in minutes) required between consecutive moves. Prevents back-to-back scheduling that doesn't account for traffic delays, customer interaction time, and vehicle inspection/handoff. Example: 40 minutes ensures drivers have breathing room between appointments."
      )
      .optional(),
    enforcement_mode: z
      .enum(['suggestion', 'forced'])
      .describe(
        'Controls whether suggested times are recommendations or hard constraints. "Suggestion" displays suggested times but allows dealer to override. "Forced" only allows suggested times to be selectable, preventing submission of non-compliant appointment times.'
      )
      .optional(),
    offset_max_minutes: z
      .number()
      .int()
      .nonnegative()
      .describe(
        'Maximum time deviation (in minutes) a dealer can adjust from a suggested appointment time. Controls how far dealers can shift appointments from algorithm-suggested slots. Example: 30-minute offset with 8:00 AM suggested slot allows 7:30 AM (30 minutes earlier), 8:00 AM (suggested time), or 8:30 AM (30 minutes later).'
      )
      .optional(),
    offset_increment_minutes: z
      .number()
      .int()
      .refine(v => [5, 10, 15].includes(v))
      .describe(
        'Defines the time increment (in minutes) for allowable offset adjustments. Prevents arbitrary times like 8:12 AM or 8:37 AM that complicate scheduling. Works in conjunction with Offset Max to control available override options. Example: 15-minute increments allow 8:00, 8:15, 8:30, 8:45, etc.'
      )
      .optional(),
  })
  .strict()
  .describe(
    'Configure how appointment times are calculated, presented, and enforced for dealers. Control driver utilization algorithms, time buffers, enforcement modes, and allowable scheduling adjustments.'
  );

export type AppointmentManagement = z.infer<typeof schema>;

export const definition = define({
  key: 'appointment_management',
  schema,
  label: 'Appointment Management',
  description:
    'Configure how appointment times are calculated, presented, and enforced for dealers. Control driver utilization algorithms, time buffers, enforcement modes, and allowable scheduling adjustments.',
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.APPOINTMENT_MANAGEMENT.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    enabled: false,
    full_planning_enabled: true,
    strategy: 'balance-drivers-v1',
    driver_count: 2,
    move_buffer_minutes: 15,
    enforcement_mode: 'suggestion',
    offset_max_minutes: 15,
    offset_increment_minutes: 15,
  },
});
